<template>
  <div class="flex-1 flex flex-col min-w-0">
    <div v-if="shouldShowStatusBadge" class="mb-3">
      <survey-status-badge :status="questionStatus" />
    </div>
    <div class="mb-6 flex-shrink-0">
      <small
        class="small text-gray-darker"
        style="font-size: 11px; text-transform: uppercase; font-weight: 700"
      >
        {{
          $t('brand-surveys.survey-question-modal.question-number', {
            number: questionIndex + 1,
          })
        }}
      </small>
      <p class="text-lg font-bold break-words">{{ questionName }}</p>
    </div>

    <div class="flex-shrink-0">
      <survey-question-modal-header
        :survey-internal-number="surveyInternalNumber"
        :user-full-name="userFullName"
        :place-name="placeName"
      />
    </div>
    <small
      class="small flex-shrink-0 text-gray-darker"
      style="font-size: 11px; text-transform: uppercase; font-weight: 700"
    >
      {{ $t('brand-surveys.survey-question-modal.answer') }}
    </small>

    <div
      class="rounded mt-2 mb-5 min-h-0 overflow-y-auto break-words"
      :class="{ 'bg-gray-lighter p-4': !canEdit }"
    >
      <div
        v-if="
          answer == null && questionType !== QuestionType.FILE && !questionFile
        "
        class="w-full h-full flex justify-center items-center text-center"
      >
        <span class="italic text-gray-darker">
          {{ $t('brand-surveys.survey-question-modal.no-data') }}
        </span>
      </div>
      <div v-else-if="questionType === QuestionType.TEXT">
        <TextInput
          v-if="canEdit"
          v-model="questionModel"
          type="textarea"
          :rows="7"
          textarea-style="resize: none; border-radius: 8px;"
        />
        <expandable-text v-else :text="answer" :max-length="250" />
      </div>
      <div v-else-if="questionType === QuestionType.RADIO">
        <RadioGroup
          v-if="canEdit"
          id="radio"
          v-model="questionModel"
          :items="questionOptions"
        />
        <survey-radio-option v-else :label="answer" />
      </div>
      <div v-else-if="questionType === QuestionType.CHECKBOX" class="space-y-2">
        <CheckboxGroup
          v-if="canEdit"
          id="checkbox"
          v-model="questionModel"
          :items="questionOptions"
        />
        <div v-else>
          <survey-checkbox-option
            v-for="option in answerOptions"
            :key="option"
            :label="option"
          />
          <span v-if="answerOptions.length === 0">
            {{ $t('brand-surveys.details.no-options-selected') }}
          </span>
        </div>
      </div>
      <div v-else-if="questionType === QuestionType.FILE">
        <survey-file-drop-zone
          :files="questionModel"
          :can-edit="canEdit"
          @add-file="handleAddFiles"
          @remove-file="removeFile"
        />
      </div>
      <div v-if="questionComment" class="mt-4">
        <div class="text-sm">
          <expandable-text :text="questionComment" :max-length="250" />
        </div>
      </div>
      <div v-if="questionType !== QuestionType.FILE" class="mt-4">
        <survey-file-drop-zone
          :files="questionFiles"
          :can-edit="canEdit"
          @add-file="handleAddAttachments"
          @remove-file="removeAttachment"
        />
      </div>
    </div>

    <select-input
      v-if="canManageStatus"
      :value="selectedStatus"
      :options="statusOptions"
      :placeholder="$t('common.select')"
      :label="$t('brand-surveys.survey-question-modal.status')"
      :clearable="false"
      :autocomplete="false"
      :multiple="false"
      :required="true"
      option-value-name="value"
      option-label-name="label"
      :disabled="!surveyUserId"
      @input="$emit('input', $event)"
    />
  </div>
</template>

<script>
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import {
  QuestionType,
  SURVEY_ANSWER_SEPARATOR,
} from '@/api/v2/requests/surveys';
import { SystemRole } from '@/api/requests/users';
import { useAuthUser } from '@/composables';
import { useRoleChecker } from '@/composables/use-role-checker';
import SurveyQuestionModalHeader from './SurveyQuestionModalHeader.vue';
import SurveyRadioOption from './SurveyRadioOption.vue';
import SurveyCheckboxOption from './SurveyCheckboxOption.vue';
import ExpandableText from '@/components/ExpandableText/ExpandableText.vue';
import { RadioGroup, SelectInput, CheckboxGroup } from '@/components/Inputs';
import SurveyStatusBadge from '@/views/Brand/Surveys/_components/SurveyStatusBadge.vue';
import TextInput from '@/components/Inputs/TextInput/TextInput.vue';
import {
  confirmFileUpload,
  createFileUpload,
  FileVisibility,
  uploadFileToS3,
  normalizeFileName,
} from '@/api/v2/requests/files';
import { v2ApiUrl } from '@/api/v2/requests';
import SurveyFileDropZone from './SurveyFileDropZone.vue';

export default defineComponent({
  name: 'SurveyQuestionDetails',
  components: {
    SurveyStatusBadge,
    SurveyQuestionModalHeader,
    SurveyRadioOption,
    SurveyCheckboxOption,
    ExpandableText,
    SelectInput,
    TextInput,
    RadioGroup,
    CheckboxGroup,
    SurveyFileDropZone,
  },
  props: {
    questionIndex: { type: Number, required: true },
    questionType: { type: String, required: true },
    questionName: { type: String, required: true },
    answer: { type: String, default: '' },
    questionComment: { type: String, default: '' },
    questionFile: { type: String, default: '' },
    surveyInternalNumber: { type: Number, default: null },
    userFullName: { type: String, default: null },
    placeName: { type: String, default: null },
    selectedStatus: { type: Object, default: null },
    statusOptions: { type: Array, required: true },
    surveyUserId: { type: String, default: null },
    questionStatus: { type: String, default: null },
    question: { type: Object, required: false },
    surveyUser: { type: Object },
    isEditor: { type: Boolean, default: false },
  },
  model: {
    prop: 'selectedStatus',
    event: 'input',
  },
  setup(props, ctx) {
    const { authUserId } = useAuthUser();
    const { checkRole, checkAnyRole } = useRoleChecker(ctx);
    const surveyId = computed(() => ctx.root.$route?.params?.surveyId ?? null);
    const { emit } = ctx;

    const canEdit = computed(() => {
      if (props.isEditor) {
        return props.questionStatus === 'REJECTED';
      }

      return (
        props.questionStatus === 'REJECTED' &&
        props.surveyUser?.user?.id &&
        authUserId.value === props.surveyUser.user.id
      );
    });

    const answerOptions = computed(() => {
      if (!props.answer || props.questionType !== QuestionType.CHECKBOX) {
        return [];
      }
      return props.answer.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean);
    });

    const answerFiles = computed(() => {
      if (!props.answer) return [];
      return props.answer.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean);
    });

    const questionFiles = computed(() => attachmentFiles.value);

    const shouldShowStatusBadge = computed(() => {
      return checkRole(SystemRole.PLACE_MANAGER).value && props.questionStatus;
    });

    const canManageStatus = checkAnyRole([
      SystemRole.SUPER_ADMIN,
      SystemRole.STRUCTURE_MANAGER,
      SystemRole.BRAND_ADMIN,
    ]);

    const questionOptions = computed(() => {
      if (canEdit.value === false) return [];
      if (
        props.questionType === QuestionType.RADIO ||
        props.questionType === QuestionType.CHECKBOX
      )
        return props.question.options.map((option) => option.label);
      return [];
    });

    const questionModel = ref(props.answer);
    const attachmentFiles = ref(
      props.questionFile
        ? props.questionFile.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean)
        : [],
    );

    watch(
      props,
      () => {
        if (props.questionType === QuestionType.FILE)
          questionModel.value = props.answer.split(SURVEY_ANSWER_SEPARATOR);
        else if (props.questionType === QuestionType.RADIO)
          questionModel.value = props.answer;
        else if (props.questionType === QuestionType.CHECKBOX) {
          const selected = props.answer.split(SURVEY_ANSWER_SEPARATOR);
          questionModel.value = props.question.options.map((option) =>
            selected.includes(option.label),
          );
        } else questionModel.value = props.answer;

        attachmentFiles.value = props.questionFile
          ? props.questionFile.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean)
          : [];
      },
      { immediate: true },
    );

    const id = `q-${props.questionIndex}`;

    const uploadFile = async (file) => {
      emit('update:is-loading', { id, value: true });
      const fileUploadHandle = await createFileUpload({
        fileName: normalizeFileName(file.name),
        fileSize: file.size,
        visibility: FileVisibility.SurveyAnswer,
        guards: {
          surveyId: surveyId.value,
        },
        shouldGenerateThumbnail: true,
      });
      await uploadFileToS3(file, fileUploadHandle);
      await confirmFileUpload(fileUploadHandle.fileId);
      emit('file-name-mapping', { [fileUploadHandle.fileId]: file.name });
      emit('update:is-loading', { id, value: false });
      return `${v2ApiUrl}/files/${fileUploadHandle.fileId}?ext=${fileUploadHandle.extension}`;
    };

    const handleAddFiles = async (event) => {
      const eventFiles = Array.from(event.target.files);

      if (eventFiles.length > 0) {
        if (props.question.allowMultiple) {
          const currentFiles = props.answer
            ? props.answer.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean)
            : [];
          const urls = await Promise.all(eventFiles.map(uploadFile));
          questionModel.value = [...currentFiles, ...urls];
        } else {
          const [file] = eventFiles;
          const url = await uploadFile(file);
          questionModel.value = [url];
        }
      }
    };

    const removeFile = (file) => {
      const index = questionModel.value.indexOf(file);
      if (index > -1) {
        questionModel.value.splice(index, 1);
      }
    };

    const handleAddAttachments = async (event) => {
      const eventFiles = Array.from(event.target.files);
      if (eventFiles.length > 0) {
        const urls = await Promise.all(eventFiles.map(uploadFile));
        attachmentFiles.value = [...attachmentFiles.value, ...urls];
        const newFiles = attachmentFiles.value.join(SURVEY_ANSWER_SEPARATOR);
        emit('attachment-change', newFiles);
      }
    };

    const removeAttachment = (file) => {
      attachmentFiles.value = attachmentFiles.value.filter((f) => f !== file);
      const newFiles = attachmentFiles.value.join(SURVEY_ANSWER_SEPARATOR);
      emit('attachment-change', newFiles);
    };

    watch(questionModel, (newAnswer) => {
      if (props.questionType === QuestionType.CHECKBOX) {
        const formattedAnswer = newAnswer
          .map((bool, idx) => (bool ? props.question.options[idx].label : null))
          .filter(Boolean)
          .join(SURVEY_ANSWER_SEPARATOR);
        emit('answer-change', formattedAnswer);
      } else if (props.questionType === QuestionType.FILE) {
        const formattedAnswer = newAnswer.join(SURVEY_ANSWER_SEPARATOR);
        emit('answer-change', formattedAnswer);
      } else {
        emit('answer-change', newAnswer);
      }
    });

    return {
      QuestionType,
      answerOptions,
      answerFiles,
      questionFiles,
      shouldShowStatusBadge,
      canManageStatus,
      canEdit,
      questionOptions,
      questionModel,
      handleAddFiles,
      removeFile,
      handleAddAttachments,
      removeAttachment,
    };
  },
});
</script>

<style scoped>
.small {
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
  @apply text-gray-darker;
}
</style>
